
<template>
  <div class="quarantine-timeline">
    <div class="timeline-header">
      <div class="header-info">
        <h2>{{ t('quarantine.timeline.title').value }}</h2>
        <div class="progress-info">
          <div class="progress-stats">
            <span class="stat-item">
              <span class="stat-label">{{ t('quarantine.timeline.currentDay').value }}:</span>
              <span class="stat-value">{{ currentDay }} / {{ totalDays }}</span>
            </span>
            <span class="stat-item">
              <span class="stat-label">{{ t('quarantine.timeline.completionPercentage').value }}:</span>
              <span class="stat-value">{{ completionPercentage }}%</span>
            </span>
            <span class="stat-item">
              <span class="stat-label">{{ t('quarantine.timeline.completedSteps').value }}:</span>
              <span class="stat-value">{{ completedStepsCount }} / {{ totalSteps }}</span>
            </span>
            <span class="stat-item">
              <span class="stat-label">{{ t('quarantine.timeline.currentTime').value }}:</span>
              <span class="stat-value">{{ currentDateTime }}</span>
            </span>
          </div>
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: `${completionPercentage}%` }"
            ></div>
          </div>
        </div>
      </div>
      
      <div class="header-actions">
        <button class="action-btn secondary" @click="resetPlan">
          {{ t('quarantine.timeline.restart').value }}
        </button>
      </div>
    </div>

    <div class="timeline-container">
      <div class="timeline">
        <div
          v-for="(step, index) in timelineSteps"
          :key="index"
          class="timeline-item"
          :class="{
            'completed': step.isCompleted,
            'current': isCurrentStep(step),
            'upcoming': isUpcomingStep(step)
          }"
        >
          <div class="timeline-marker">
            <div class="marker-icon">
              <span v-if="step.isCompleted">✅</span>
              <span v-else-if="isCurrentStep(step)">⏳</span>
              <span v-else>📌</span>
            </div>
            <div class="timeline-connector"></div>
          </div>

          <div class="timeline-content">
            <div class="step-header">
              <div class="step-info">
                <h4 class="step-title">{{ t(step.name).value }}</h4>
                <div class="step-meta">
                  <span class="step-day" :title="getDayTooltip(step.day)">{{ step.day }}</span>
                  <span class="step-type" :class="step.type">{{ getStepTypeLabel(step.type) }}</span>
                </div>
              </div>
              
              <div class="step-actions">
                <button
                  v-if="!step.isCompleted && isCurrentStep(step)"
                  class="complete-btn"
                  @click="showStepDetail(step)"
                >
                  {{ t('quarantine.timeline.recordComplete').value }}
                </button>
                <button
                  v-else-if="step.isCompleted"
                  class="view-btn"
                  @click="showStepDetail(step)"
                >
                  {{ t('quarantine.timeline.viewDetails').value }}
                </button>
                <span v-else class="upcoming-label">{{ t('quarantine.timeline.upcoming').value }}</span>
              </div>
            </div>

            <!-- 美化后的必做操作列表 -->
            <div v-if="step.actions && step.actions.length > 0" class="actions-section">
              <h5 class="section-subtitle">{{ t('quarantine.timeline.stepDetail.actions').value }}</h5>
              <div class="actions-list">
                <div
                  v-for="(action, actionIndex) in step.actions"
                  :key="actionIndex"
                  class="action-item"
                >
                  <div class="action-number">{{ actionIndex + 1 }}</div>
                  <div class="action-content">
                    <span class="action-text">{{ t(action).value }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 药品信息 -->
            <div v-if="step.drugs && step.drugs.length > 0" class="medications-section">
              <h5>{{ t('quarantine.timeline.medicationUsage').value }}</h5>
              <div class="medication-list">
                <div
                  v-for="(drug, drugIndex) in step.drugs"
                  :key="drugIndex"
                  class="medication-item"
                >
                  <div class="med-info">
                    <span class="med-name">{{ getMedicationName(drug.name) }}</span>
                    <span class="med-dosage">
                      <span v-if="drug.dose_ml_per_L">{{ drug.dose_ml_per_L }} ml/L ({{ (drug.dose_ml_per_L * waterVolume).toFixed(2) }}ml)</span>
                      <span v-if="drug.dose_ml_per_10L">{{ drug.dose_ml_per_10L }} ml/10L ({{ (drug.dose_ml_per_10L * waterVolume / 10).toFixed(2) }}ml)</span>
                      <span v-if="drug.dose_mg_per_L">{{ drug.dose_mg_per_L }} mg/L ({{ (drug.dose_mg_per_L * waterVolume).toFixed(2) }}mg)</span>
                      <span v-if="drug.targetPPM">{{ drug.targetPPM }} ppm</span>
                      <span v-if="drug.tabletPerX_L">{{ drug.tabletPerX_L[0] }}片/{{ drug.tabletPerX_L[1] }}L ({{ Math.ceil(waterVolume / drug.tabletPerX_L[1] * drug.tabletPerX_L[0]) }}片)</span>
                    </span>
                  </div>
                  <div v-if="drug.note" class="med-notes">
                    {{ drug.note }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 警告信息 -->
            <div v-if="step.safetyNotes && step.safetyNotes.length > 0" class="warnings-section">
              <div class="warning-item">
                <span class="warning-icon">⚠️</span>
                <span class="warning-text">{{ t(step.safetyNotes[0]).value }}</span>
              </div>
            </div>

            <!-- 完成状态 -->
            <div v-if="step.isCompleted" class="completion-info">
              <div class="completion-meta">
                <span class="completion-date">{{ t('quarantine.timeline.completedAt').value }} {{ formatDate(step.completedDate) }}</span>
                <span
                  v-if="step.fishStatus"
                  class="status-badge"
                  :class="step.fishStatus.appetite"
                >
                  {{ getAppetiteLabel(step.fishStatus.appetite) }}
                </span>
              </div>
              
              <div v-if="step.fishStatus && step.fishStatus.symptoms.length > 0" class="symptoms-list">
                <span class="symptoms-label">{{ t('quarantine.timeline.symptomsLabel').value }}</span>
                <span class="symptoms-text">{{ step.fishStatus.symptoms.join(', ') }}</span>
              </div>
              
              <div v-if="step.fishStatus && step.fishStatus.notes" class="notes-section">
                <span class="notes-label">{{ t('quarantine.timeline.notesLabel').value }}</span>
                <span class="notes-text">{{ step.fishStatus.notes }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!selectedPlan" class="empty-state">
      <div class="empty-icon">🛡️</div>
      <h3>{{ t('quarantine.timeline.emptyState.title').value }}</h3>
      <p>{{ t('quarantine.timeline.emptyState.description').value }}</p>
      <button class="primary-btn" @click="$emit('show-selector')">
        {{ t('quarantine.timeline.emptyState.selectPlanButton').value }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '../composables/useI18n'
import { type QuarantinePlan, type QuarantineStep, MEDICATION_SPECS } from '../composables/useQuarantinePlan'

const { t, language } = useI18n()

// Props
interface Props {
  selectedPlan: QuarantinePlan | null
  waterVolume: number
  startDate: string
  currentDay: number
  completedSteps: Set<number>
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'show-step-detail': [step: QuarantineStep | null]
  'reset-plan': []
  'show-selector': []
}>()

// 计算属性
const totalDays = computed(() => {
  return props.selectedPlan?.duration || 0
})

const totalSteps = computed(() => {
  return props.selectedPlan?.steps.length || 0
})

const completedStepsCount = computed(() => {
  return props.completedSteps.size
})

const completionPercentage = computed(() => {
  if (!totalSteps.value) return 0
  return Math.round((completedStepsCount.value / totalSteps.value) * 100)
})

const currentDateTime = computed(() => {
  const now = new Date()
  const locale = language.value === 'zh-CN' ? 'zh-CN' : language.value === 'de-DE' ? 'de-DE' : 'en-US'
  return now.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
})

const timelineSteps = computed(() => {
  if (!props.selectedPlan) return []
  
  return props.selectedPlan.steps.map((step, index) => ({
    ...step,
    isCompleted: props.completedSteps.has(index)
  }))
})

// 方法
const isCurrentStep = (step: QuarantineStep) => {
  // 解析天数范围，如 'T+0~T+3' 或 'T+3'
  const dayMatch = step.day.match(/T\+(\d+)(?:~T\+(\d+))?/)
  if (!dayMatch) return false
  
  const startDay = parseInt(dayMatch[1])
  const endDay = dayMatch[2] ? parseInt(dayMatch[2]) : startDay
  
  return props.currentDay >= startDay && props.currentDay <= endDay && !props.completedSteps.has(getStepIndex(step))
}

const isUpcomingStep = (step: QuarantineStep) => {
  // 解析天数范围，如 'T+0~T+3' 或 'T+3'
  const dayMatch = step.day.match(/T\+(\d+)(?:~T\+(\d+))?/)
  if (!dayMatch) return false
  
  const startDay = parseInt(dayMatch[1])
  return props.currentDay < startDay
}

const getStepIndex = (step: QuarantineStep) => {
  return props.selectedPlan?.steps.findIndex(s => s.day === step.day) || -1
}

const getStepTypeLabel = (type: string) => {
  const key = `quarantine.timeline.stepType.${type}`
  const translated = t(key).value
  return translated !== key ? translated : type
}

const getMedicationName = (medKey: string) => {
  return MEDICATION_SPECS[medKey as keyof typeof MEDICATION_SPECS]?.name || medKey
}

const getAppetiteLabel = (appetite: string) => {
  const key = `quarantine.timeline.appetite.${appetite}`
  const translated = t(key).value
  return translated !== key ? translated : appetite
}

const formatDate = (dateString?: string) => {
  if (!dateString) return ''
  const locale = language.value === 'zh-CN' ? 'zh-CN' : language.value === 'de-DE' ? 'de-DE' : 'en-US'
  return new Date(dateString).toLocaleDateString(locale)
}

const getDayTooltip = (dayString: string) => {
  const match = dayString.match(/T\+(\d+)(?:~T\+(\d+))?/)
  if (!match) return dayString
  
  const startDay = parseInt(match[1])
  const endDay = match[2] ? parseInt(match[2]) : startDay
  
  if (startDay === endDay) {
    return t('quarantine.timeline.dayTooltip.single').value.replace('${startDay}', startDay.toString())
  } else {
    return t('quarantine.timeline.dayTooltip.range').value.replace('${startDay}', startDay.toString()).replace('${endDay}', endDay.toString())
  }
}

const showStepDetail = (step: QuarantineStep | null) => {
  emit('show-step-detail', step)
}

const resetPlan = () => {
  emit('reset-plan')
}
</script>

<style scoped>
.quarantine-timeline {
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
}

.timeline-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(102, 126, 234, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  position: relative;
  overflow: hidden;
}

.timeline-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-info {
  flex: 1;
}

.header-info h2 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 700;
}

.progress-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.progress-stats {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  min-width: 120px;
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
}

.stat-value {
  color: #2c3e50;
  font-weight: 700;
  font-size: 1.25rem;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.header-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 160px;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 0.9rem;
  position: relative;
  overflow: hidden;
}

.action-btn:not(.secondary) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.action-btn.secondary {
  background: rgba(255, 255, 255, 0.8);
  color: #6c757d;
  border: 1px solid rgba(102, 126, 234, 0.2);
  backdrop-filter: blur(10px);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.action-btn:active {
  transform: translateY(0);
}

.timeline-container {
  position: relative;
}

.timeline {
  position: relative;
}

.timeline-item {
  display: flex;
  margin-bottom: 2.5rem;
  position: relative;
  transition: all 0.3s ease;
}

.timeline-item:hover {
  transform: translateX(8px);
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-item:last-child .timeline-connector {
  display: none;
}

.timeline-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 2rem;
  position: relative;
  z-index: 2;
}

.marker-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 3px solid #e9ecef;
  font-size: 1.4rem;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.timeline-item.completed .marker-icon {
  border-color: #28a745;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: #ffffff;
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
}

.timeline-item.current .marker-icon {
  border-color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  animation: pulse-glow 2s infinite;
}

.timeline-item.upcoming .marker-icon {
  border-color: #6c757d;
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

.timeline-connector {
  flex: 1;
  width: 3px;
  background: linear-gradient(to bottom, #e9ecef, #dee2e6);
  margin: 0.75rem 0;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.timeline-item.completed .timeline-connector {
  background: linear-gradient(to bottom, #28a745, #20c997);
}

.timeline-content {
  flex: 1;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid rgba(102, 126, 234, 0.1);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.timeline-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.timeline-item.completed .timeline-content {
  border-color: rgba(40, 167, 69, 0.3);
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%);
  box-shadow: 0 6px 25px rgba(40, 167, 69, 0.15);
}

.timeline-item.completed .timeline-content::before {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  opacity: 1;
}

.timeline-item.current .timeline-content {
  border-color: rgba(102, 126, 234, 0.3);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.2);
  transform: scale(1.02);
}

.timeline-item.current .timeline-content::before {
  opacity: 1;
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  50% {
    box-shadow: 0 6px 30px rgba(102, 126, 234, 0.6);
  }
  100% {
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
}

.step-info {
  flex: 1;
}

.step-title {
  color: #2c3e50;
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.step-meta {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.step-day {
  color: #667eea;
  font-weight: 600;
  font-size: 0.9rem;
  background: rgba(102, 126, 234, 0.1);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.step-type {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-type.medication {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(220, 53, 69, 0.2) 100%);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

.step-type.water_change {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 123, 255, 0.2) 100%);
  color: #17a2b8;
  border: 1px solid rgba(0, 123, 255, 0.3);
}

.step-type.observation {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(40, 167, 69, 0.2) 100%);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.step-type.feeding {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.2) 100%);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.step-type.transfer {
  background: linear-gradient(135deg, rgba(111, 66, 193, 0.1) 0%, rgba(111, 66, 193, 0.2) 100%);
  color: #6f42c1;
  border: 1px solid rgba(111, 66, 193, 0.3);
}

.step-actions {
  flex-shrink: 0;
  min-width: 100px; /* 确保按钮有足够空间 */
}

.complete-btn, .view-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap; /* 防止文字换行 */
}

.complete-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.complete-btn:hover {
  background: linear-gradient(135deg, #218838 0%, #1ea085 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
}

.view-btn {
  background: rgba(255, 255, 255, 0.8);
  color: #6c757d;
  border: 1px solid rgba(102, 126, 234, 0.2);
  backdrop-filter: blur(10px);
}

.view-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-color: rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

.upcoming-label {
  color: #adb5bd;
  font-size: 0.875rem;
  font-weight: 500;
  font-style: italic;
  padding: 0.5rem 1rem;
  background: rgba(108, 117, 125, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.step-description {
  color: #666666;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.medications-section {
  margin-bottom: 1rem;
}

.medications-section h5 {
  color: #333333;
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.medication-list {
  display: flex;
  flex-direction: column;

  gap: 0.5rem;
}

.medication-item {
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.med-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.med-name {
  color: #333333;
  font-weight: 500;
}

.med-dosage {
  color: #667eea;
  font-size: 0.875rem;
}

.med-notes {
  color: #666666;
  font-size: 0.875rem;
  font-style: italic;
}

.warnings-section {
  margin-bottom: 1rem;
}

.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
}

.warning-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.warning-text {
  color: #ffc107;
  font-size: 0.875rem;
  line-height: 1.4;
  flex: 1;
}

.completion-info {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.completion-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.completion-date {
  color: #666666;
  font-size: 0.875rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.good {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.status-badge.normal {
  background: rgba(0, 123, 255, 0.1);
  color: #17a2b8;
}

.status-badge.poor {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.status-badge.none {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.symptoms-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.symptoms-label {
  color: #666666;
}

.symptoms-text {
  color: #333333;
}

.notes-section {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.notes-label {
  color: #666666;
  flex-shrink: 0;
}

.notes-text {
  color: #333333;
  line-height: 1.4;
  flex: 1;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  color: #333333;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #666666;
  margin-bottom: 1.5rem;
}

.primary-btn {
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
}

/* 必做操作样式 */
.actions-section {
  margin-bottom: 1.5rem;
}

.section-subtitle {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e8f5e8;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 4px rgba(40, 167, 69, 0.1);
}

.action-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
  border-color: #28a745;
}

.action-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(40, 167, 69, 0.3);
}

.action-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 24px;
}

.action-text {
  color: #2c3e50;
  line-height: 1.5;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .timeline-header {
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
  }
  
  .header-actions {
    flex-direction: row;
    width: 100%;
  }
  
  .action-btn {
    flex: 1;
  }
  
  .progress-stats {
    gap: 1rem;
  }
  
  .step-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .step-actions {
    align-self: stretch;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  
  .complete-btn, .view-btn {
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    min-width: 100px;
  }
  
  .timeline-marker {
    margin-right: 1rem;
  }
  
  .marker-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .timeline-content {
    padding: 1.25rem;
  }
}
</style>
